import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import InternalHeader from '../InternalHeader';
import Sidebar from '../Sidebar';
import '../Patients/CreatePatient.css';
import { FaSave } from 'react-icons/fa';
import AccessControl from '../AccessControl';
import LinkMap from '../LinkMap/LinkMap';

const CreateDentist = () => {
    const navigate = useNavigate();
    const [isSidebarVisible, setIsSidebarVisible] = useState(true);
    const [internalUsers, setInternalUsers] = useState([]); // State to store fetched internal users
    const linkData = [
        { label: 'Dentists', path: '/dentists' },
        { label: 'Create Dentist', path: '/create-dentist' },
    ];

    const toggleSidebar = () => {
        setIsSidebarVisible(!isSidebarVisible);
    };

    const [dentist, setDentist] = useState({
        firstName: "",
        middleName: "",
        lastName: "",
        sex: "",
        birthdate: '',
        address: '',
        ContactInformation: '',
        email: '',
        internalUser: '', // Added internal user field
    });

    const [errors, setErrors] = useState({});

    // Fetch internal users from the backend
    useEffect(() => {
        const fetchInternalUsers = async () => {
            try {
                const response = await fetch('https://api.hgcdental.clinic/api/internalusers');
                const data = await response.json();
                if (response.ok) {
                    setInternalUsers(data);
                } else {
                    console.error('Error fetching internal users:', data.error);
                }
            } catch (error) {
                console.error('Error:', error);
            }
        };
        fetchInternalUsers();
    }, []);

    const validateForm = () => {
        const newErrors = {};

        if (!dentist.firstName.trim()) newErrors.firstName = 'First name is required';
        if (!dentist.lastName.trim()) newErrors.lastName = 'Last name is required';
        if (!dentist.sex) newErrors.sex = 'Sex is required';
        if (!dentist.birthdate) newErrors.birthdate = 'Date of Birth is required';
        if (!dentist.address.trim()) newErrors.address = 'Address is required';
        if (!dentist.ContactInformation.trim()) newErrors.ContactInformation = 'Contact Information is required';
        if (!dentist.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(dentist.email)) {
            newErrors.email = 'Email is invalid';
        }
        if (!dentist.internalUser) newErrors.internalUser = 'Internal User is required';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            alert('Please fill out all required fields correctly.');
            return;
        }

        const response = await fetch('https://api.hgcdental.clinic/api/dentists', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(dentist),
        });

        const json = await response.json();

        if (response.ok) {
            window.alert('Dentist added successfully!');
            navigate('/dentists');
        } else {
            console.error('Error:', json.error);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        setDentist((prevState) => ({
            ...prevState,
            [name]: value,
        }));

        setErrors((prevErrors) => ({
            ...prevErrors,
            [name]: '',
        }));
    };

    return (
        <AccessControl>
            <div className={`create-patient-container ${isSidebarVisible ? 'sidebar-visible' : 'sidebar-hidden'}`}>
                <InternalHeader moduleName="Create Dentist" toggleSidebar={toggleSidebar} />
                <div className="create-patient-content">
                    <Sidebar isSidebarVisible={isSidebarVisible} />
                    <div className="create-patient">
                        <LinkMap
                            links={linkData}
                            moduleName="Category"
                            moduleIcon="doctor"
                        />
                        <div>
                            <button className="create-patient-button" onClick={handleSubmit}>
                                <FaSave /> Save
                            </button>
                        </div>
                        <div className="create-patient-form">
                            <h2>Create New Dentist</h2>

                            {/* Name Field */}
                            <div className="input-group">
                                <label className="label">First Name</label>
                                <input
                                    type="text"
                                    name="firstName" // Unique name for First Name
                                    value={dentist.firstName}
                                    onChange={handleChange}
                                    className="input-field"
                                />
                                {errors.firstName && <span className="error-message">{errors.firstName}</span>}
                            </div>

                            <div className="input-group">
                                <label className="label">Middle Name</label>
                                <input
                                    type="text"
                                    name="middleName" // Unique name for Middle Name
                                    value={dentist.middleName}
                                    onChange={handleChange}
                                    className="input-field"
                                />
                                {errors.middleName && <span className="error-message">{errors.middleName}</span>}
                            </div>

                            <div className="input-group">
                                <label className="label">Last Name</label>
                                <input
                                    type="text"
                                    name="lastName" // Unique name for Last Name
                                    value={dentist.lastName}
                                    onChange={handleChange}
                                    className="input-field"
                                />
                                {errors.lastName && <span className="error-message">{errors.lastName}</span>}
                            </div>

                            {/* Sex Field */}
                            <div className="input-group">
                                <label className="label">Sex</label>
                                <select name="sex" value={dentist.sex} onChange={handleChange} className="input-field">
                                    <option value="">Select</option>
                                    <option value="Male">Male</option>
                                    <option value="Female">Female</option>
                                </select>
                                {errors.sex && <span className="error-message">{errors.sex}</span>}
                            </div>

                            {/* Date of Birth Field */}
                            <div className="input-group">
                                <label className="label">Date of Birth</label>
                                <input type="date" name="birthdate" value={dentist.birthdate} onChange={handleChange} className="input-field" />
                                {errors.birthdate && <span className="error-message">{errors.birthdate}</span>}
                            </div>

                            {/* Address Field */}
                            <div className="input-group">
                                <label className="label">Address</label>
                                <input type="text" name="address" value={dentist.address} onChange={handleChange} className="input-field" />
                                {errors.address && <span className="error-message">{errors.address}</span>}
                            </div>

                            {/* Contact Information Field */}
                            <div className="input-group">
                                <label className="label">Contact Information</label>
                                <input type="tel" name="ContactInformation" value={dentist.ContactInformation} onChange={handleChange} className="input-field" />
                                {errors.ContactInformation && <span className="error-message">{errors.ContactInformation}</span>}
                            </div>

                            {/* Email Field */}
                            <div className="input-group">
                                <label className="label">Email</label>
                                <input type="text" name="email" value={dentist.email} onChange={handleChange} className="input-field" />
                                {errors.email && <span className="error-message">{errors.email}</span>}
                            </div>

                            {/* Internal User Selection */}
                            <div className="input-group">
                                <label className="label">Internal User Account</label>
                                <select name="internalUser" value={dentist.internalUser} onChange={handleChange} className="input-field">
                                    <option value="">Select Internal User</option>
                                    {internalUsers.map((user) => (
                                        <option key={user._id} value={user._id}>
                                            {user.firstName} {user.lastName} ({user.email})
                                        </option>
                                    ))}
                                </select>
                                {errors.internalUser && <span className="error-message">{errors.internalUser}</span>}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </AccessControl>
    );
};

export default CreateDentist;
